import { memo, useEffect, useMemo, useState } from "react";
import { Provider } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router";

import config from "../config";
import { LocaleStorageKey } from "../constants";
import LocalStorage from "../LocalStorage";
import store from "../model/store";

import ResizeHandler from "./controls/ResizeHandler";
import Navigator from "./Navigator";
import Permalink from "./Permalink";

interface NavigatorHistory {
  replace: (url: string, search: string) => void;
}

interface NavigatorWithRouterProps {
  [key: string]: unknown;
  useLocalStorage: boolean;
}

function NavigatorWithRouter({
  useLocalStorage,
  ...props
}: NavigatorWithRouterProps) {
  const { stationId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Compute the redirect URL synchronously (only when useLocalStorage is true).
  // This avoids calling setState inside an effect.
  const redirectUrl = useMemo<null | string>(() => {
    if (!useLocalStorage) {
      return null;
    }

    // If a stationId is in url, ignores localstorage values,
    // because it probably means the user explicitly navigated
    // to a specific station.
    if (stationId) {
      return null;
    }

    const localStorage = new LocalStorage(config.localStorageKey);

    // If no station id in the url and no values in localstorage,
    // then redirects to the default station.
    if (
      !stationId &&
      !localStorage.getItem(LocaleStorageKey.Uic) &&
      !localStorage.getItem(LocaleStorageKey.SearchParams) &&
      config.initialStation
    ) {
      // Redirect to the default uic
      return `/navigator/${config.initialStation}`;
    }

    // if there is values in localstorage use it
    const searchParamsLs =
      localStorage.getItem(LocaleStorageKey.SearchParams) ?? "";
    const uicLs = localStorage.getItem(LocaleStorageKey.Uic) ?? "";
    let url = "/navigator/";
    url += uicLs ? `${uicLs}` : "";
    url += searchParamsLs ? `?${searchParamsLs}` : "";

    if (url !== `${window.location.pathname}${window.location.search}`) {
      return url;
    }
    return null;
  }, [stationId, useLocalStorage]);

  const [init] = useState<boolean>(() => !useLocalStorage || !redirectUrl);

  const history = useMemo<NavigatorHistory>(() => {
    return {
      replace: (url: string, searchString: string) => {
        let newUrl = url;
        if (searchString) {
          newUrl = `${window.location?.pathname}?${searchString}`;
        }

        if (
          newUrl !== `${window.location?.pathname}?${window.location?.search}`
        ) {
          void navigate(newUrl, { replace: true });
        }
      },
    };
  }, [navigate]);

  // Only when the url is /navigator,
  // we redirect to the settings from localstorage.
  useEffect(() => {
    if (!init && redirectUrl) {
      // Reload the page with the good paramters to be sure the state is correctly restored.
      window.location.replace(redirectUrl);
    }
  }, [init, redirectUrl]);

  // Update localstorage when stationId changes and the app is ready.
  useEffect(() => {
    if (useLocalStorage && init) {
      const localStorage = new LocalStorage(config.localStorageKey);
      if (stationId) {
        localStorage.setItem(LocaleStorageKey.Uic, stationId);
      } else {
        localStorage.removeItem(LocaleStorageKey.Uic);
      }
    }
  }, [stationId, useLocalStorage, init]);

  // Update localstorage when searchParams changes and the app is ready.
  useEffect(() => {
    if (useLocalStorage && init) {
      const localStorage = new LocalStorage(config.localStorageKey);
      if (searchParams) {
        localStorage.setItem(
          LocaleStorageKey.SearchParams,
          searchParams.toString(),
        );
      } else {
        localStorage.removeItem(LocaleStorageKey.SearchParams);
      }
    }
  }, [searchParams, useLocalStorage, init]);

  if (!init) {
    return null;
  }

  return (
    <Provider store={store}>
      <div id="app">
        <ResizeHandler observe="#app" />
        <Navigator history={history} stationId={stationId} {...props} />
        <Permalink history={history} startUpdating={init} />
      </div>
    </Provider>
  );
}

export default memo(NavigatorWithRouter);
